import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { cn } from "@/lib/utils";
const formatDate = (dateString?: string) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric"
  });
};
const getReadTime = (content?: string) => {
  if (!content) return "3 min read";
  const words = content.split(/\s+/).length;
  const minutes = Math.ceil(words / 200);
  return `${minutes} min read`;
};
function InsightsStrategyConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="space-y-12">
        {}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="h-[300px] w-full animate-pulse rounded-xl bg-muted lg:col-span-7 lg:h-[450px]" />
          <div className="flex flex-col justify-center space-y-6 lg:col-span-5">
            <div className="h-4 w-32 animate-pulse rounded bg-muted" />
            <div className="h-12 w-full animate-pulse rounded bg-muted" />
            <div className="h-24 w-full animate-pulse rounded bg-muted" />
            <div className="h-10 w-40 animate-pulse rounded bg-muted" />
          </div>
        </div>
        {}
        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {[...Array(3)].map((_, i) => <div key={i} className="space-y-4">
              <div className="aspect-[3/2] w-full animate-pulse rounded-xl bg-muted" />
              <div className="h-6 w-3/4 animate-pulse rounded bg-muted" />
              <div className="h-4 w-full animate-pulse rounded bg-muted" />
            </div>)}
        </div>
      </div>;
  }
  if (error) {
    return <div className="flex h-60 flex-col items-center justify-center rounded-xl border border-dashed p-8 text-center">
        <p className="text-muted-foreground">Unable to load insights at this time.</p>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="flex h-60 flex-col items-center justify-center rounded-xl border border-dashed p-8 text-center">
        <p className="text-muted-foreground">No insights found.</p>
      </div>;
  }
  const featuredPost = posts[0];
  const gridPosts = posts.slice(1);
  return <div className="space-y-16">
      {}
      <div data-index={0} className="group grid grid-cols-1 gap-8 overflow-hidden rounded-2xl border bg-card transition-all hover:shadow-md lg:grid-cols-12">
        <div className="relative h-64 w-full overflow-hidden lg:col-span-7 lg:h-auto lg:min-h-[450px]">
          <Link to={featuredPost.link || "#"} className="block h-full w-full">
            {featuredPost.featuredImage ? <img src={featuredPost.featuredImage} alt={featuredPost.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                <span className="text-lg font-medium">No Image</span>
              </div>}
          </Link>
          <div className="absolute top-6 left-6">
            {featuredPost.categories && featuredPost.categories.length > 0 && <Badge className="bg-accent text-accent-foreground hover:bg-accent/90 border-none px-3 py-1.5 text-xs font-bold uppercase tracking-wider shadow-sm">
                {featuredPost.categories[0].name}
              </Badge>}
          </div>
        </div>
        
        <div className="flex flex-col justify-center p-6 lg:col-span-5 lg:p-12">
          <div className="mb-6 flex items-center gap-4 text-xs font-medium text-muted-foreground uppercase tracking-wide">
            <span className="flex items-center gap-1.5">
              <SafeHydrate><Calendar className="h-3.5 w-3.5" /></SafeHydrate>
              {formatDate(featuredPost.date)}
            </span>
            <span className="h-1 w-1 rounded-full bg-muted-foreground/40" />
            <span className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              {getReadTime(featuredPost.content)}
            </span>
          </div>
          
          <Link to={featuredPost.link || "#"} className="group-hover:text-primary">
            <h3 className="mb-6 font-serif text-3xl font-bold leading-tight text-foreground lg:text-4xl xl:text-5xl">
              {featuredPost.title}
            </h3>
          </Link>
          
          <div className="mb-8 line-clamp-3 text-base text-muted-foreground md:text-lg leading-relaxed" dangerouslySetInnerHTML={{
          __html: featuredPost.excerpt || ""
        }} />
          
          <div className="mt-auto">
            <Link to={featuredPost.link || "#"}>
              <Button variant="outline" className="group/btn border-primary/20 hover:border-primary hover:bg-primary hover:text-primary-foreground">
                Read Full Article
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {}
      {gridPosts.length > 0 && <div className="grid grid-cols-1 gap-x-8 gap-y-12 md:grid-cols-2 lg:grid-cols-3">
          {gridPosts.map((post, i) => <article key={post.id} data-index={i + 1} className="group flex flex-col space-y-5">
              <div className="relative overflow-hidden rounded-xl shadow-sm transition-shadow hover:shadow-md">
                <Link to={post.link || "#"}>
                  <AspectRatio ratio={3 / 2}>
                    {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                        <span className="text-sm">No Image</span>
                      </div>}
                  </AspectRatio>
                </Link>
                <div className="absolute top-4 left-4">
                  {post.categories && post.categories.length > 0 && <Badge className="bg-accent text-accent-foreground hover:bg-accent/90 border-none shadow-sm font-semibold">
                      {post.categories[0].name}
                    </Badge>}
                </div>
              </div>

              <div className="flex flex-1 flex-col space-y-3">
                <div className="flex items-center gap-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  <span>{formatDate(post.date)}</span>
                  <span className="h-1 w-1 rounded-full bg-muted-foreground/30" />
                  <span>{getReadTime(post.content)}</span>
                </div>

                <Link to={post.link || "#"}>
                  <h4 className="font-serif text-2xl font-bold leading-snug text-foreground transition-colors group-hover:text-primary/80">
                    {post.title}
                  </h4>
                </Link>

                <div className="line-clamp-3 text-sm text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{
            __html: post.excerpt || ""
          }} />
                
                <div className="pt-2 mt-auto">
                  <Link to={post.link || "#"} className="inline-flex items-center text-sm font-bold text-primary underline-offset-4 hover:underline">
                    Read more
                  </Link>
                </div>
              </div>
            </article>)}
        </div>}
    </div>;
}
export default function InsightsStrategy() {
  const wp_query_params = {
    per_page: 7,
    orderby: "date",
    order: "desc",
    status: "publish"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="insights-strategy" className="bg-background py-24 md:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-16 flex flex-col items-start justify-between gap-8 border-b border-border pb-8 md:flex-row md:items-end">
          <div className="max-w-3xl">
            <h2 className="font-serif text-4xl font-bold tracking-tight text-foreground md:text-5xl lg:text-6xl">
              Insights & Strategy
            </h2>
            <p className="mt-6 text-lg text-muted-foreground md:text-xl leading-relaxed">
              Expert perspectives on copywriting, content strategy, and digital growth. 
              Unpacking the nuances of effective messaging for modern businesses.
            </p>
          </div>
          <div className="hidden md:block shrink-0">
            <Link to="/TheContentArchivePage">
              <Button variant="ghost" className="group text-base font-medium">
                View All Articles
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>

        <PostsProvider wp_query={{
        per_page: 7,
        orderby: "date",
        order: "desc",
        status: ["publish"]
      }}>
          <InsightsStrategyConsumer />
        </PostsProvider>

        <div className="mt-16 flex justify-center md:hidden">
          <Link to="/TheContentArchivePage">
            <Button variant="outline" className="w-full sm:w-auto">
              View All Articles
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}